import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuRenderFn,
  MenuTextMatch,
  TriggerFn,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ESCAPE_COMMAND,
  TextNode,
} from 'lexical';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { $createPresentationVariableNode } from '@/components/TextEditor/plugins/PresentationVariablePlugin/PresentationVariableNode';

import { useSlideEditorPromptContext } from './SlideEditor.promptContext';
import {
  useFilteredVariableNames,
  VARIABLE_TRIGGER_REGEX,
} from './SlideEditor.variableTypeahead.utils';
import {
  useVirtualSelectionAnchor,
  VariableTypeaheadPopper,
} from './SlideEditor.variableTypeaheadMenu';

// ---------- VariableMenuOption ----------

class VariableMenuOption extends MenuOption {
  readonly variableName: string;

  constructor(variableName: string) {
    super(variableName);
    this.variableName = variableName;
  }
}

// ---------- Trigger function ----------

/**
 * Custom trigger that fires on `{`.
 *
 * We cannot use `useBasicTypeaheadTriggerMatch` because it excludes
 * punctuation and whitespace from the characters allowed after the trigger.
 * Variable names contain spaces (e.g. "Client one first name"), so we need
 * a permissive regex.
 *
 * The regex also provides `}` dismissal for free: when the user types `}`,
 * the `[^}]*` portion fails, the function returns null, and the plugin
 * closes the menu.
 */
function useVariableTriggerMatch(): TriggerFn {
  return useCallback((text: string): MenuTextMatch | null => {
    const match = VARIABLE_TRIGGER_REGEX.exec(text);

    if (match === null) return null;

    // match[1] is the first capture group (e.g. "{Household"), match[2] is the
    // second (e.g. "Household"). Both are guaranteed to exist when the regex
    // matches, but TypeScript types regex groups as possibly undefined.
    const fullMatch = match[1] ?? '';
    const matchingString = match[2] ?? '';
    const leadOffset = match.index + (match[0].length - fullMatch.length);

    return {
      leadOffset,
      matchingString,
      replaceableString: fullMatch,
    };
  }, []);
}

// ---------- Options hook ----------

function useVariableOptions(queryString: string | null): VariableMenuOption[] {
  const filteredNames = useFilteredVariableNames(queryString);

  return useMemo(
    () => filteredNames.map((name) => new VariableMenuOption(name)),
    [filteredNames]
  );
}

// ---------- Selection handler ----------

function useHandleSelectOption() {
  return useCallback(
    (
      option: VariableMenuOption,
      textNodeContainingQuery: TextNode | null,
      closeMenu: () => void
    ) => {
      const variableText = `{${option.variableName}}`;

      // Create a PresentationVariableNode directly instead of inserting
      // plain text. This avoids PresentationVariablePlugin's node transform
      // (which converts {Variable} TextNodes into PresentationVariableNodes)
      // from interfering with cursor placement after insertion.
      const variableNode = $createPresentationVariableNode(variableText);

      if (textNodeContainingQuery !== null) {
        textNodeContainingQuery.replace(variableNode);
      } else {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertNodes([variableNode]);
        }
      }

      // Place cursor after the inserted variable node so the user can
      // continue typing.
      variableNode.selectNext(0, 0);

      closeMenu();
    },
    []
  );
}

// ---------- Menu render function ----------

/**
 * Wraps the shared VariableTypeaheadPopper for use with
 * LexicalTypeaheadMenuPlugin's MenuRenderFn API. We need a wrapper component
 * (not just a callback) so we can call the useVirtualSelectionAnchor hook.
 */
function LexicalVariableMenu({
  options,
  selectedIndex,
  selectOptionAndCleanUp,
  setHighlightedIndex,
}: {
  options: VariableMenuOption[];
  selectedIndex: number | null;
  selectOptionAndCleanUp: (option: VariableMenuOption) => void;
  setHighlightedIndex: (index: number) => void;
}) {
  // The LexicalTypeaheadMenuPlugin positions its anchor div using a DOM
  // Range captured during a startTransition callback. Inside a
  // transform:scale() container (ScalableSlide), the range can become
  // stale by the time the anchor position is applied, causing it to land
  // at (0,0). The shared virtual anchor reads the live native selection
  // instead, which always returns correct viewport coordinates.
  const virtualAnchor = useVirtualSelectionAnchor();

  const handleSelect = useCallback(
    (_name: string, index: number) => {
      // The index maps back to the VariableMenuOption, which is safe
      // because options and the menu's string list are derived from the
      // same filtered array in the same order.
      selectOptionAndCleanUp(options[index]!);
    },
    [options, selectOptionAndCleanUp]
  );

  const getExtraMenuItemProps = useCallback(
    (index: number) => ({
      ref: (el: HTMLLIElement | null) => options[index]?.setRefElement(el),
    }),
    [options]
  );

  return (
    <VariableTypeaheadPopper
      anchorEl={virtualAnchor}
      options={options.map((o) => o.variableName)}
      selectedIndex={selectedIndex}
      onSelect={handleSelect}
      onHighlight={setHighlightedIndex}
      idPrefix="typeahead-item"
      getExtraMenuItemProps={getExtraMenuItemProps}
    />
  );
}

function useMenuRenderFn(): MenuRenderFn<VariableMenuOption> {
  return useCallback(
    (
      _anchorElementRef,
      { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex, options }
    ) => {
      if (options.length === 0) {
        return null;
      }

      return (
        <LexicalVariableMenu
          options={options}
          selectedIndex={selectedIndex}
          selectOptionAndCleanUp={selectOptionAndCleanUp}
          setHighlightedIndex={setHighlightedIndex}
        />
      );
    },
    []
  );
}

// ---------- Escape propagation guard ----------

/**
 * When the typeahead menu is open, Lexical's built-in KEY_ESCAPE_COMMAND
 * handler calls `event.stopImmediatePropagation()` (which only prevents
 * other listeners on the *same* element) but does NOT call
 * `event.stopPropagation()`. The native keydown event therefore bubbles
 * to parent DOM elements and can close the slide editor.
 *
 * This hook registers a higher-priority Escape command that stops the
 * native event from propagating, then returns false so the plugin's own
 * handler still runs and closes the menu.
 */
function useStopEscapePropagation(isMenuOpen: boolean) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!isMenuOpen) return;

    return editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      (event) => {
        event.stopPropagation();
        // Return false so the TypeaheadMenu's own Escape handler still fires
        // and closes the menu.
        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, isMenuOpen]);
}

// ---------- Assembled plugin ----------

export function VariableTypeaheadPlugin() {
  const promptContext = useSlideEditorPromptContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useStopEscapePropagation(isMenuOpen);

  const triggerFn = useVariableTriggerMatch();
  const options = useVariableOptions(queryString);
  const handleSelectOption = useHandleSelectOption();
  const menuRenderFn = useMenuRenderFn();

  const handleOpen = useCallback(() => {
    setIsMenuOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  // Hide the typeahead when editing a prompt on a template —
  // variables are presentation-time values that don't belong in prompts.
  if (promptContext?.isTemplate && promptContext.customPrompt) {
    return null;
  }

  return (
    <LexicalTypeaheadMenuPlugin<VariableMenuOption>
      options={options}
      onQueryChange={setQueryString}
      onSelectOption={handleSelectOption}
      onOpen={handleOpen}
      onClose={handleClose}
      triggerFn={triggerFn}
      menuRenderFn={menuRenderFn}
      parent={document.body}
      preselectFirstItem
      ignoreEntityBoundary
    />
  );
}
